import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatCOP, formatDate, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Disclaimer } from "../components/Disclaimer";
import PaymentCountdown from "../components/PaymentCountdown";
import EntregablesPanel from "../components/EntregablesPanel";
import ComprobanteUploadBlock from "../components/ComprobanteUploadBlock";
import CaseFlowStepper from "../components/CaseFlowStepper";
import CaseSummaryCard from "../components/CaseSummaryCard";
import FlowGuide from "../components/FlowGuide";
import ConfirmDialog from "../components/ConfirmDialog";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: { label: "Pago pendiente", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  confirmed: { label: "Pago confirmado", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  failed: { label: "Pago fallido", cls: "bg-rose-50 text-rose-800 border-rose-300" },
  refunded: { label: "Reembolsado", cls: "bg-gray-100 text-gray-700 border-gray-300" },
};

export default function PaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseDoc, setCaseDoc] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [mpInitiating, setMpInitiating] = useState(false);
  // Fase 12 Sprint 2: cuota gratuita
  const [quota, setQuota] = useState(null);
  const [quotaInitiating, setQuotaInitiating] = useState(false);
  // Iter24.c: modal de confirmación (reemplaza window.confirm)
  const [quotaConfirmOpen, setQuotaConfirmOpen] = useState(false);

  // Variant selection state
  const [variantSelected, setVariantSelected] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [submittingVariant, setSubmittingVariant] = useState(false);
  const [showVariantSelector, setShowVariantSelector] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [caseRes, quotaRes] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get(`/me/quota`).catch(() => ({ data: null })),
      ]);
      const c = caseRes.data;
      setCaseDoc(c);
      setQuota(quotaRes?.data || null);

      // Solo permitir esta página para estados de pago.
      if (!["nivel1_payment_pending", "nivel1_paid"].includes(c.state)) {
        navigate(`/cases/${id}`, { replace: true });
        return;
      }

      // Si el caso ya tiene variant_nivel1 definido, significa que ya pasó por el selector
      if (c.variant_nivel1) {
        setVariantSelected(c.variant_nivel1);
        setShowVariantSelector(false);
        // Init/get payment.
        try {
          const { data: p } = await api.post(`/cases/${id}/payments/nivel1/init`);
          setPayment(p);
        } catch (initErr) {
          // Si no se puede inicializar (estado distinto), intenta GET.
          try {
            const { data: p2 } = await api.get(`/cases/${id}/payments/nivel1`);
            setPayment(p2);
          } catch {
            setError(formatApiError(initErr?.response?.data?.detail) || "No se pudo iniciar el pago");
          }
        }
      } else {
        // El user aún no eligió variante, mostrar selector
        setShowVariantSelector(true);
      }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el caso");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Polling: si payment está confirmed, monitorear estado del caso para redirigir al detail
  // cuando esté delivered (especialmente útil para variant=auto que cambia rápido)
  useEffect(() => {
    if (!payment || payment.status !== "confirmed") return;
    if (!caseDoc) return;
    if (caseDoc.state === "nivel1_delivered" || caseDoc.state === "nivel2_offer_sent") {
      // Listo, redirigir
      navigate(`/cases/${id}`, { replace: true });
      return;
    }
    const t = setInterval(async () => {
      try {
        const { data: c } = await api.get(`/cases/${id}`);
        setCaseDoc(c);
        if (c.state === "nivel1_delivered" || c.state === "nivel2_offer_sent") {
          clearInterval(t);
          toast.success("¡Tu Diagnóstico está listo!");
          navigate(`/cases/${id}`, { replace: true });
        }
      } catch (pollErr) {
        // Errores transitorios de polling: no interrumpir UX, pero loggear en dev.
        if (process.env.NODE_ENV === "development") {
          console.warn("[PaymentPage] transient polling error:", pollErr);
        }
      }
    }, 3000);
    return () => clearInterval(t);
  }, [payment, caseDoc, id, navigate]);

  const retryPayment = async () => {
    setRetrying(true);
    try {
      // Forzar un nuevo init (si el último estaba failed, el backend creará uno nuevo
      // pending al no existir pending|confirmed).
      const { data: p } = await api.post(`/cases/${id}/payments/nivel1/init`);
      setPayment(p);
      toast.success("Nuevo intento de pago iniciado");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo reiniciar el pago");
    } finally {
      setRetrying(false);
    }
  };

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(payment?.reference || "");
      toast.success("Referencia copiada");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const payWithMercadoPago = async () => {
    setMpInitiating(true);
    try {
      const { data } = await api.post(`/cases/${id}/payments/nivel1/init-mercadopago`);
      if (data?.init_point) {
        // Redirigir al checkout MP en la misma pestaña
        window.location.href = data.init_point;
      } else {
        toast.error("No se pudo obtener el enlace de MercadoPago");
        setMpInitiating(false);
      }
    } catch (e) {
      const detail = formatApiError(e?.response?.data?.detail);
      if (detail && detail.toLowerCase().includes("no está habilitado")) {
        toast.error("MercadoPago no está disponible en este momento. Usa otro método de pago.");
      } else {
        toast.error(detail || "No se pudo iniciar el pago con MercadoPago");
      }
      setMpInitiating(false);
    }
  };

  const useComplimentaryQuota = () => {
    if (!quota || (quota.diagnoses_remaining ?? 0) <= 0) {
      toast.error("No tienes cuota gratuita disponible para Diagnósticos.");
      return;
    }
    setQuotaConfirmOpen(true);
  };

  const confirmUseComplimentaryQuota = async () => {
    setQuotaConfirmOpen(false);
    setQuotaInitiating(true);
    try {
      await api.post(`/cases/${id}/payments/nivel1/use-complimentary-quota`);
      toast.success("Cuota gratuita aplicada. Generando tu Diagnóstico…");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo aplicar la cuota");
    } finally {
      setQuotaInitiating(false);
    }
  };

  const handleVariantSubmit = async () => {
    if (!variantSelected) {
      toast.error("Debes elegir una variante");
      return;
    }
    if (variantSelected === "auto" && !disclaimerAccepted) {
      toast.error("Debes aceptar el disclaimer para continuar con la variante automatizada");
      return;
    }
    setSubmittingVariant(true);
    try {
      await api.post(`/cases/${id}/request-nivel1`, {
        variant: variantSelected,
        disclaimer_accepted: variantSelected === "auto" ? disclaimerAccepted : false,
      });
      toast.success("Variante seleccionada. Inicializando pago...");
      // Recargar caso y pago
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar la variante");
    } finally {
      setSubmittingVariant(false);
    }
  };

  if (loading) {
    return (
      <div className="container-prose py-12">
        <div className="border border-gray-200 p-8 text-sm text-gray-500">Cargando…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-prose py-12">
        <div className="max-w-2xl mx-auto">
          <Link to={`/cases/${id}`} className="text-sm text-gray-500 hover:text-primary">← Volver al caso</Link>
          <div className="mt-4 border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm" data-testid="payment-error">{error}</div>
        </div>
      </div>
    );
  }

  if (!payment) {
    // Si llegamos aquí sin variant elegido (caso edge), redirigimos al detail del caso
    // para que el usuario pase por la prevalidación → selector de variante.
    if (showVariantSelector) {
      return (
        <div data-testid="payment-page-no-variant" className="container-prose py-12">
          <div className="max-w-2xl mx-auto">
            <Link to={`/cases/${id}`} className="text-sm text-gray-500 hover:text-primary">← Volver al caso</Link>
            <div className="mt-4 border border-amber-200 bg-amber-50 p-6">
              <h2 className="font-serif text-xl text-gray-900 mb-2">Selecciona tu plan de Diagnóstico</h2>
              <p className="text-sm text-gray-700 mb-4">
                Antes de pagar debes elegir entre el Diagnóstico automatizado o el Diagnóstico con
                revisión humana. Te llevamos a la página de prevalidación donde podrás elegir.
              </p>
              <Button asChild className="rounded-sm" data-testid="back-to-prev">
                <Link to={`/cases/${id}/prevalidation`}>Elegir mi plan</Link>
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const st = STATUS_STYLE[payment.status] || STATUS_STYLE.pending;

  return (
    <div data-testid="payment-page" className="container-prose py-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-2">
          <Link to={`/cases/${id}`} className="text-sm text-gray-500 hover:text-primary">← Volver al caso</Link>
        </div>

        {/* Iter20 — Summary + Stepper */}
        <CaseSummaryCard caseDoc={caseDoc} showLink={false} />
        <div className="mb-4">
          <CaseFlowStepper caseId={id} state={caseDoc?.state || "nivel1_payment_pending"} />
        </div>

        {/* Iter25 — Guía de auto-orientación (resuelve "¿tengo cuota?", "¿qué entrega?", "¿cómo paso?") */}
        <div className="mb-6">
          <FlowGuide level="n1" quota={quota} state={caseDoc?.state} caseId={id} />
        </div>

        <div className="mb-8 pb-6 border-b border-gray-200">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">
            Pago — Diagnóstico Precontractual
          </p>
          <h1 className="font-serif text-2xl text-gray-900 mb-3">Confirma tu pago</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span data-testid={`payment-status-${payment.status}`}
                  className={`uppercase tracking-widest border px-3 py-1 ${st.cls}`}>
              {st.label}
            </span>
            <span className="text-gray-500">Valor estimado del proceso: {formatCOP(caseDoc?.valor_estimado_cop)}</span>
          </div>
        </div>

        {/* Variant elegido (informativo) */}
        {variantSelected && (
          <div
            className={`border p-4 mb-6 ${variantSelected === "auto" ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}
            data-testid="variant-selected-banner"
          >
            <p className="text-sm font-medium text-gray-900">
              Plan elegido: {variantSelected === "auto" ? "Diagnóstico Automatizado (sin revisión humana)" : "Diagnóstico con revisión humana experta"}
              {" — "}
              {formatCOP(variantSelected === "auto" ? 199000 : 489000)}
            </p>
            {variantSelected === "auto" && (
              <p className="text-xs text-amber-900 mt-1">
                Sin revisión humana — el documento se descargará inmediatamente tras confirmarse el pago.
                La responsabilidad de su uso es del usuario.
              </p>
            )}
          </div>
        )}

        {/* Monto + referencia */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-gray-200 bg-white mb-6">
          <div className="p-6 md:border-r border-gray-200">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Monto a pagar</p>
            <p data-testid="payment-amount" className="font-serif text-3xl text-gray-900">{formatCOP(payment.amount_cop)}</p>
            <p className="text-xs text-gray-500 mt-1">IVA y comisiones incluidos.</p>
          </div>
          <div className="p-6">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Referencia</p>
            <div className="flex items-center gap-3">
              <code data-testid="payment-reference" className="font-mono text-sm bg-gray-100 px-2 py-1 border border-gray-300">{payment.reference}</code>
              <Button size="sm" variant="outline" className="rounded-sm border-gray-300 h-8 text-xs" onClick={copyRef} data-testid="copy-reference">Copiar</Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Incluye esta referencia en el concepto del pago.</p>
          </div>
        </div>

        {/* Instrucciones por provider */}
        {payment.status === "pending" && (
          <div data-testid="payment-instructions" className="border border-gray-200 bg-white p-7 mb-6">
            <h2 className="font-serif text-xl text-gray-900 mb-4">Cómo pagar</h2>

            {/* Fase 12 Sprint 2: Cuota gratuita (si admin asignó >0) */}
            {quota && (quota.diagnoses_remaining ?? 0) > 0 && (
              <div className="border-2 border-emerald-300 bg-emerald-50/40 p-5 mb-5" data-testid="quota-pay-block">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <p className="text-[10px] uppercase tracking-widest text-emerald-800 font-medium mb-1">
                      Cuota gratuita disponible
                    </p>
                    <h3 className="font-serif text-lg text-gray-900 mb-1">
                      Usar 1 Diagnóstico gratuito
                    </h3>
                    <p className="text-sm text-gray-700">
                      Te quedan <strong>{quota.diagnoses_remaining}</strong> de {quota.diagnoses_limit} cuotas
                      gratuitas de Diagnóstico este mes ({quota.year_month}). Asignadas por el equipo de Procure.
                    </p>
                  </div>
                  <Button
                    onClick={useComplimentaryQuota}
                    disabled={quotaInitiating || mpInitiating}
                    className="rounded-sm h-12 px-6 bg-emerald-700 hover:bg-emerald-800"
                    data-testid="quota-pay-button"
                  >
                    {quotaInitiating ? "Aplicando…" : "Usar cuota gratuita"}
                  </Button>
                </div>
              </div>
            )}

            {/* MercadoPago — opción primaria (tarjeta, PSE, Efecty) */}
            <div className="border border-primary/40 bg-primary/[0.03] p-5 mb-5" data-testid="mp-pay-block">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <p className="text-[10px] uppercase tracking-widest text-primary font-medium mb-1">
                    {quota && (quota.diagnoses_remaining ?? 0) > 0 ? "O paga" : "Recomendado"}
                  </p>
                  <h3 className="font-serif text-lg text-gray-900 mb-1">Pagar con MercadoPago</h3>
                  <p className="text-sm text-gray-700">
                    Tarjeta crédito/débito, PSE, Efecty o Baloto. Confirmación automática en minutos.
                  </p>
                </div>
                <Button
                  onClick={payWithMercadoPago}
                  disabled={mpInitiating || quotaInitiating}
                  className="rounded-sm h-12 px-8"
                  data-testid="mp-pay-button"
                >
                  {mpInitiating ? "Abriendo…" : "Pagar con MercadoPago"}
                </Button>
              </div>
            </div>

            {/* Separador con texto */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-[10px] uppercase tracking-widest text-gray-400">o paga manualmente</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Manual payment options — show ALL available methods */}
            <div className="space-y-4">
              {/* Bank transfer */}
              {payment.bank_transfer_info && (
                <div data-testid="provider-bank-transfer" className="border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Transferencia bancaria</h4>
                  <table className="w-full text-sm border border-gray-200 bg-white">
                    <tbody>
                      {Object.entries(payment.bank_transfer_info).map(([k, v]) => (
                        <tr key={k} className="border-b border-gray-200 last:border-b-0">
                          <td className="px-4 py-2 text-xs uppercase tracking-widest text-gray-500 bg-gray-50 w-1/3">{k}</td>
                          <td className="px-4 py-2 text-gray-800">{String(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-sm text-gray-700 mt-3">
                    Realiza la transferencia incluyendo la referencia <code className="font-mono">{payment.reference}</code> en el concepto.
                  </p>
                </div>
              )}

              {/* QR Bre-B (generado dinámicamente desde llave configurada) */}
              {(payment.qr_data_url || payment.qr_image_url) && (
                <div data-testid="provider-qr" className="border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">QR Bre-B</h4>
                  <img
                    src={payment.qr_data_url || payment.qr_image_url}
                    alt="QR Bre-B"
                    className="max-w-[280px] border border-gray-200 bg-white"
                  />
                  {payment.bre_b_key && (
                    <p className="text-xs text-gray-600 mt-2">
                      Llave Bre-B: <code className="font-mono">{payment.bre_b_key}</code>
                    </p>
                  )}
                  <p className="text-sm text-gray-700 mt-3">
                    Escanea el QR con tu app bancaria Bre-B. Incluye la referencia <code className="font-mono">{payment.reference}</code> en el concepto.
                  </p>
                </div>
              )}

              {/* External payment link */}
              {payment.payment_link && (
                <div data-testid="provider-external-link" className="border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Pasarela externa</h4>
                  <Button asChild className="rounded-sm h-12 px-8" data-testid="external-pay-button">
                    <a href={payment.payment_link} target="_blank" rel="noopener noreferrer">Pagar ahora</a>
                  </Button>
                  <p className="text-xs text-gray-500 mt-3">
                    Se abrirá una nueva pestaña. Al completar el pago, espera la confirmación. Procesamos pagos en máx. 24 horas hábiles.
                  </p>
                </div>
              )}

              {/* Extra instructions */}
              {payment.extra_instructions && (
                <div data-testid="provider-extra-instructions" className="border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Instrucciones adicionales</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{payment.extra_instructions}</p>
                </div>
              )}

              {/* Fallback: contact email (always shown) */}
              <div data-testid="provider-manual" className="border border-gray-200 bg-gray-50 p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Otra forma de pago</h4>
                <p className="text-sm text-gray-700 mb-4">
                  Para coordinar el pago, escribe a{" "}
                  <a className="text-primary underline" href={`mailto:${payment.admin_contact_email}?subject=Pago Diagnóstico referencia ${payment.reference}&body=Hola,%0A%0ASolicito coordinación de pago para la referencia ${payment.reference}.%0A%0AGracias.`}>
                    {payment.admin_contact_email}
                  </a>{" "}
                  con la referencia <code className="font-mono">{payment.reference}</code> y el detalle de tu caso.
                </p>
                <Button asChild className="rounded-sm" data-testid="contact-mailto">
                  <a href={`mailto:${payment.admin_contact_email}?subject=Pago Diagnóstico referencia ${payment.reference}`}>Contactar a operaciones</a>
                </Button>
              </div>

              {/* P1: Subir comprobante de pago (Bre-B / transferencia) */}
              <ComprobanteUploadBlock
                caseId={id}
                nivel="nivel1"
                paymentStatus={payment.status}
                alreadyUploaded={Boolean(payment.comprobante_url || payment.comprobante_uploaded_at)}
                uploadedAt={payment.comprobante_uploaded_at}
                filename={payment.comprobante_filename}
                onUploaded={load}
              />
            </div>
          </div>
        )}

        {/* Tras tu pago */}
        {payment.status === "pending" && (
          <>
            <PaymentCountdown level="nivel1" testid="payment-countdown-n1" />
            <div className="border border-gray-200 bg-gray-50 p-6 mb-6">
              <h3 className="font-serif text-lg text-gray-900 mb-2">Tras tu pago</h3>
              <p className="text-sm text-gray-700">
                Una vez efectuado, recibirás confirmación en máximo 24 horas hábiles. El estado se actualizará automáticamente en esta página.
              </p>
            </div>
          </>
        )}

        {/* Confirmed */}
        {payment.status === "confirmed" && (
          <div data-testid="payment-confirmed-block" className="border border-emerald-200 bg-emerald-50/40 p-6 mb-6">
            <h3 className="font-serif text-lg text-emerald-900 mb-2">Pago confirmado</h3>
            <p className="text-sm text-emerald-900">
              Tu caso está en cola de procesamiento. Te notificaremos cuando el diagnóstico esté listo para revisión humana.
            </p>
            {payment.confirmed_at && (
              <p className="text-xs text-emerald-800/80 mt-2">Confirmado el {formatDate(payment.confirmed_at)}</p>
            )}
          </div>
        )}

        {/* Failed */}
        {payment.status === "failed" && (
          <div data-testid="payment-failed-block" className="border border-rose-200 bg-rose-50/40 p-6 mb-6">
            <h3 className="font-serif text-lg text-rose-900 mb-2">Pago fallido</h3>
            {payment.fail_reason && <p className="text-sm text-rose-900 mb-4">{payment.fail_reason}</p>}
            <Button onClick={retryPayment} disabled={retrying} className="rounded-sm" data-testid="payment-retry">
              {retrying ? "Reintentando…" : "Reintentar pago"}
            </Button>
          </div>
        )}

        {/* Refunded */}
        {payment.status === "refunded" && (
          <div data-testid="payment-refunded-block" className="border border-gray-300 bg-gray-50 p-6 mb-6">
            <h3 className="font-serif text-lg text-gray-900 mb-2">Pago reembolsado</h3>
            {payment.refund_reason && <p className="text-sm text-gray-700 mb-4">{payment.refund_reason}</p>}
            <p className="text-sm text-gray-700 mb-4">
              Puedes iniciar un nuevo intento de pago si deseas continuar con el Diagnóstico.
            </p>
            <Button onClick={retryPayment} disabled={retrying} className="rounded-sm" data-testid="payment-retry-after-refund">
              {retrying ? "Iniciando…" : "Iniciar nuevo pago"}
            </Button>
          </div>
        )}

        {/* Disclaimer reforzado */}
        <div className="mt-8">
          <Disclaimer dataTestId="payment-disclaimer" />
          <p className="mt-3 text-xs text-gray-500 italic">
            La entrega del Diagnóstico requiere confirmación humana del pago y posterior revisión humana experta antes de la descarga. Insumo de apoyo. No reemplaza el concepto de un abogado titulado.
          </p>
        </div>
      </div>
      <ConfirmDialog
        open={quotaConfirmOpen}
        onOpenChange={setQuotaConfirmOpen}
        title="Usar cuota gratuita"
        description={
          <>
            Vas a usar <strong>1</strong> de tus{" "}
            <strong>{quota?.diagnoses_remaining ?? 0}</strong> cuotas gratuitas de
            <strong> Diagnóstico</strong> de este mes. La cuota se descontará
            inmediatamente y se generará el Diagnóstico Nivel 1.
            <br />
            <br />
            ¿Deseas continuar?
          </>
        }
        confirmLabel="Sí, usar cuota"
        cancelLabel="Cancelar"
        onConfirm={confirmUseComplimentaryQuota}
        loading={quotaInitiating}
        testIdPrefix="quota-confirm-n1"
      />
    </div>
  );
}
